import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Trophy, Clock, Target, Flame, Calendar, TrendingUp, Award, Activity } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useAccessibility } from '../contexts/AccessibilityContext';

/**
 * Interfaces para las estadísticas
 */
interface UserGeneralStats {
  total_partidas: number;
  tiempo_total: number;
  juegos_completados: number;
  racha_dias: number;
  mejor_racha: number;
  dias_activos_mes: number;
  promedio_tiempo: number;
}

interface ProgressByGame {
  juego_id: number;
  nombre_juego: string;
  codigo_juego: string;
  total_partidas: number;
  partidas_completadas: number;
  mejor_tiempo: number | null;
  promedio_tiempo: number;
  ultima_partida: Date | null;
}

interface ActivityDay {
  fecha: string;
  partidas: number;
  tiempo_total: number;
}

interface RecentProgress {
  id: number;
  juego_nombre: string;
  tipo_nivel: string;
  tiempo: number;
  completado: boolean;
  fecha: Date;
}

interface Achievement {
  id: string;
  titulo: string;
  descripcion: string;
  conseguido: boolean;
  fecha?: Date;
}

interface WeeklyEvolution {
  semana_actual: {
    partidas: number;
    tiempo_total: number;
  };
  semana_anterior: {
    partidas: number;
    tiempo_total: number;
  };
  mejora_partidas: number;
  mejora_tiempo: number;
}

interface UserAdvancedStats {
  general: UserGeneralStats;
  progressByGame: ProgressByGame[];
  activityLast30Days: ActivityDay[];
  recentProgress: RecentProgress[];
  achievements: Achievement[];
  evolution: WeeklyEvolution;
}

/**
 * Componente de tarjeta de estadística
 */
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
}

const StatCard = ({ icon, label, value, color }: StatCardProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-gradient-to-br ${color} p-6 rounded-2xl shadow-lg`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-white/80 text-sm mb-2">{label}</p>
        <p className="text-white text-3xl font-bold">{value}</p>
      </div>
      <div className="text-white/80">{icon}</div>
    </div>
  </motion.div>
);

/**
 * Formatear tiempo en formato hh:mm:ss
 */
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

/**
 * Formatear fecha en español
 */
const formatDate = (date: Date): string => {
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Formatear fecha corta para gráficas
 */
const formatDateShort = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
  });
};

/**
 * Página principal de estadísticas de usuario
 */
export default function UserStatsPage() {
  const { highContrast } = useAccessibility();
  const chartPrimary = highContrast ? '#FFE600' : '#8b5cf6';
  const chartSecondary = highContrast ? '#00ff88' : '#10b981';

  const [stats, setStats] = useState<UserAdvancedStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/user/stats/advanced');

      if (response.data.ok) {
        setStats(response.data.data);
      } else {
        toast.error('Error al cargar estadísticas');
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <p className="text-white text-xl">No se pudieron cargar las estadísticas</p>
      </div>
    );
  }

  const { general, progressByGame, activityLast30Days, recentProgress, achievements, evolution } = stats;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2">Mi Progreso Detallado</h1>
          <p className="text-gray-300">Visualiza tu rendimiento y logros</p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatCard
            icon={<Trophy className="w-8 h-8" />}
            label="Total Partidas"
            value={general.total_partidas}
            color="from-purple-600 to-purple-800"
          />
          <StatCard
            icon={<Clock className="w-8 h-8" />}
            label="Tiempo Total"
            value={formatTime(general.tiempo_total)}
            color="from-blue-600 to-blue-800"
          />
          <StatCard
            icon={<Target className="w-8 h-8" />}
            label="Juegos Completados"
            value={general.juegos_completados}
            color="from-green-600 to-green-800"
          />
          <StatCard
            icon={<Flame className="w-8 h-8" />}
            label="Racha Actual"
            value={`${general.racha_dias} días`}
            color="from-orange-600 to-orange-800"
          />
          <StatCard
            icon={<Award className="w-8 h-8" />}
            label="Mejor Racha"
            value={`${general.mejor_racha} días`}
            color="from-red-600 to-red-800"
          />
          <StatCard
            icon={<Calendar className="w-8 h-8" />}
            label="Días Activos (mes)"
            value={general.dias_activos_mes}
            color="from-teal-600 to-teal-800"
          />
        </div>

        {/* Gráfica de Actividad */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6" />
            Actividad de los Últimos 30 Días
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={activityLast30Days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis
                dataKey="fecha"
                tickFormatter={formatDateShort}
                stroke="#ffffff80"
                style={{ fontSize: '12px' }}
              />
              <YAxis stroke="#ffffff80" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                }}
                labelFormatter={(label) => formatDateShort(label)}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="partidas"
                stroke={chartPrimary}
                strokeWidth={2}
                name="Partidas"
                dot={{ fill: chartPrimary }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Gráfica de Progreso por Juego */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Trophy className="w-6 h-6" />
            Progreso por Juego
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={progressByGame.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis
                dataKey="nombre_juego"
                stroke="#ffffff80"
                style={{ fontSize: '12px' }}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis stroke="#ffffff80" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
              <Legend />
              <Bar dataKey="total_partidas" fill={chartPrimary} name="Total Partidas" />
              <Bar dataKey="partidas_completadas" fill={chartSecondary} name="Completadas" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Evolución Semanal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Semana Actual vs Anterior
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Partidas</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">{evolution.semana_actual.partidas}</span>
                    {evolution.mejora_partidas > 0 && (
                      <span className="text-green-400 text-sm">
                        +{evolution.mejora_partidas} ↑
                      </span>
                    )}
                    {evolution.mejora_partidas < 0 && (
                      <span className="text-red-400 text-sm">
                        {evolution.mejora_partidas} ↓
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-400">
                  Semana anterior: {evolution.semana_anterior.partidas}
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Tiempo Total</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">
                      {formatTime(evolution.semana_actual.tiempo_total)}
                    </span>
                    {evolution.mejora_tiempo > 0 && (
                      <span className="text-green-400 text-sm">
                        +{formatTime(evolution.mejora_tiempo)} ↑
                      </span>
                    )}
                    {evolution.mejora_tiempo < 0 && (
                      <span className="text-red-400 text-sm">
                        {formatTime(Math.abs(evolution.mejora_tiempo))} ↓
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-400">
                  Semana anterior: {formatTime(evolution.semana_anterior.tiempo_total)}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Logros */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Logros ({achievements.filter(a => a.conseguido).length}/{achievements.length})
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
              {achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className={`p-3 rounded-lg ${
                    achievement.conseguido
                      ? 'bg-yellow-500/20 border border-yellow-500/30'
                      : 'bg-gray-700/30 border border-gray-600/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={achievement.conseguido ? 'text-yellow-400' : 'text-gray-500'}>
                      <Trophy className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-semibold ${achievement.conseguido ? 'text-white' : 'text-gray-400'}`}>
                        {achievement.titulo}
                      </h4>
                      <p className="text-sm text-gray-400">{achievement.descripcion}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Actividad Reciente */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6"
        >
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6" />
            Actividad Reciente
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-3 px-4 text-gray-300 font-semibold">Juego</th>
                  <th className="text-left py-3 px-4 text-gray-300 font-semibold">Nivel</th>
                  <th className="text-left py-3 px-4 text-gray-300 font-semibold">Tiempo</th>
                  <th className="text-left py-3 px-4 text-gray-300 font-semibold">Estado</th>
                  <th className="text-left py-3 px-4 text-gray-300 font-semibold">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {recentProgress.map((progress) => (
                  <tr key={progress.id} className="border-b border-white/10 hover:bg-white/5">
                    <td className="py-3 px-4 text-white">{progress.juego_nombre}</td>
                    <td className="py-3 px-4 text-gray-300">{progress.tipo_nivel}</td>
                    <td className="py-3 px-4 text-gray-300">{formatTime(progress.tiempo)}</td>
                    <td className="py-3 px-4">
                      {progress.completado ? (
                        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                          Completado
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full text-sm">
                          En progreso
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-sm">{formatDate(progress.fecha)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.6);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.8);
        }
      `}</style>
    </div>
  );
}
